import 'url-search-params-polyfill';
import axios from 'axios';
import { is, ifElse, isEmpty } from 'ramda';
import * as Config from '../config/config';

const isNotEmptyDot = s => (isEmpty(s) ? s : `${s}.`);

/**
 * Object :: Object
 * Takes an object with nested objects and returns a plane object
 * with its nested objects as dot notation keys
 * @param {Object} obj Just an object that does not contains arrays
 * @param {String} prefix Prefix notation
 */
export const flattenObjectToDotNotation = (obj, prefix = '') =>
  Object.keys(obj).reduce(
    (ac, cK) =>
      ifElse(
        is(Object),
        v => ({ ...ac, ...flattenObjectToDotNotation(v, `${isNotEmptyDot(prefix)}${cK}`) }),
        v => ({ ...ac, [`${isNotEmptyDot(prefix)}${cK}`]: v })
      )(obj[cK]),
    {}
  );

/**
 * String :: <String|Number>
 * Returns uri param for given string
 * @param {String} uriParam
 */
export const retrieveUriParam = uriParam =>
  new URLSearchParams(window.location.search).get(uriParam);

export const retrieveAllUriParams = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const results = {};
  searchParams.forEach((val, key) => {
    results[key] = val;
  });
  return results;
};

/**
 * String :: void!
 * Takes user to url provided as String param
 * @param {String} url
 */
export const redirectWindowToo = url => {
  window.location.href = url;
};

export const isDevelopment = () => process.env.NODE_ENV === 'development';

export const isProd = () => process.env.NODE_ENV === 'production';

/**
 * LocalStorage static class.
 * Queries LocalStorage and mutates its values
 *
 */
export class LocalStorage {
  static getLocalStorage() {
    const storage = window.localStorage || localStorage;

    if (!localStorage) {
      throw new Error('Browser does not support local storage');
    }

    return storage;
  }

  static set(key, value) {
    LocalStorage.getLocalStorage().setItem(key, value);
    return LocalStorage.getLocalStorage();
  }

  static get(key) {
    return LocalStorage.getLocalStorage().getItem(key);
  }

  static setObject(key, value) {
    LocalStorage.set(key, JSON.stringify(value));
    return LocalStorage.getLocalStorage();
  }

  static getObject(key) {
    return JSON.parse(LocalStorage.get(key) || '{}');
  }

  static getArray(key) {
    return JSON.parse(LocalStorage.get(key) || '[]');
  }

  static remove(key) {
    LocalStorage.getLocalStorage().removeItem(key);
    return LocalStorage.getLocalStorage();
  }
}

export const LOCAL_STORAGE_PROVIDERS = [{ provide: LocalStorage, useClass: LocalStorage }];

/**
 * Object :: String
 * takes an object containing { redirectUri, idToken, accessToken, refreshToken }
 * returns url as String
 * @param {Object} param0
 */
export const createRedirectUrl = ({ redirectUri, idToken, accessToken, refreshToken }) => {
  const queryParamStr = `id_token=${idToken}&access_token=${accessToken}&refresh_token=${refreshToken}`;
  return redirectUri.indexOf('?') > -1
    ? `${redirectUri}&${queryParamStr}`
    : `${redirectUri}?${queryParamStr}`;
};

/**
 * Object::Promise
 * Takes an object containing email and password
 * returns a promise resolving to data provided by the client's migration endpoint
 *
 * @param {Object} params
 */
export const migrateExistingUser = ({
  email,
  password,
  firstName,
  lastName,
  params,
  redirectUri,
  isNew
}) => {
  const clientId = retrieveUriParam('client_id');
  const options = {
    method: 'post',
    url: `${Config.AUTH_API_BASE_URL}/clients/${clientId}/migrate`,
    headers: {
      'X-API-KEY': Config.AUTH_API_KEY
    },
    data: {
      email: email.toLowerCase(),
      password,
      firstName,
      lastName,
      params,
      redirectUri,
      isNew
    }
  };

  return new Promise((resolve, reject) => {
    axios(options)
      .then(res => {
        resolve(res);
      })
      .catch(() => {
        reject(new Error('Invalid username or password.'));
      });
  });
};

/**
 * String::<String>
 * Takes an email address string for the existing user
 * returns a promise resolving to data provided by the client's migration endpoint
 *
 * @param {Object} params
 */
export const migrateExistingUserForgotPassword = email => {
  const clientId = retrieveUriParam('client_id');
  const options = {
    method: 'post',
    url: `${Config.AUTH_API_BASE_URL}/clients/${clientId}/migrate-forgot-password`,
    headers: {
      'X-API-KEY': Config.AUTH_API_KEY
    },
    data: { email: email.toLowerCase() }
  };

  return new Promise((resolve, reject) => {
    axios(options)
      .then(res => {
        resolve(res);
      })
      .catch(() => {
        reject(new Error('Username/client id combination not found.'));
      });
  });
};

/**
 * Object::Promise
 * Takes an object containing clientId and redirectUri
 * returns a promise resolving to data provided by the update user endpoint
 *
 * @param {Object} params
 */
export const updateUserLastAction = async ({ clientId, redirectUri, email, params }) => {
  const options = {
    method: 'put',
    url: `${Config.AUTH_API_BASE_URL}/users/${encodeURIComponent(email.toLowerCase())}`,
    headers: {
      'X-API-KEY': Config.AUTH_API_KEY
    },
    data: {
      lastActionAttempt: { clientId, redirectUri, params }
    }
  };

  if (clientId && redirectUri) {
    return axios(options);
  }
  return null;
};
export const updateUserClaims = ({ clientId, email }) => {
  const options = {
    method: 'post',
    url: `${Config.AUTH_API_BASE_URL}/users/${encodeURIComponent(email.toLowerCase())}/claims`,
    headers: {
      'X-API-KEY': Config.AUTH_API_KEY
    },
    data: {
      claim: clientId
    }
  };
  return axios(options);
};
export const updateUsersTokens = ({ email, idToken, accessToken, refreshToken }) => {
  const options = {
    method: 'post',
    url: `${Config.AUTH_API_BASE_URL}/users/${encodeURIComponent(email.toLowerCase())}/tokens`,
    headers: {
      'X-API-KEY': Config.AUTH_API_KEY
    },
    data: {
      email: email.toLowerCase(),
      idToken,
      accessToken,
      refreshToken
    }
  };
  return axios(options);
};

/**
 * Object::Promise
 * Takes an object containing clientId and redirectUri
 * returns a promise resolving to data provided by the resend temp password endpoint
 *
 * @param {Object} params
 */
export const resendTempPassword = ({ email, clientId, redirectUri }) => {
  const options = {
    method: 'post',
    url: `${Config.AUTH_API_BASE_URL}/admin/users/resend`,
    headers: {
      'X-API-KEY': Config.AUTH_API_KEY
    },
    data: { email: email.toLowerCase(), clientId, redirectUri }
  };

  return axios(options);
};



// WEBPACK FOOTER //
// ./src/utils/helpers.js